import classnames from "classnames";
import { forwardRef, type ReactNode } from "preact/compat";
import "./index.scss";

export type ComboboxGroupButtonProps = {
  children?: ({ open }: { open: boolean }) => ReactNode;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  variant?: "contained" | "outlined";
  name?: string;
  labels?: string[];
  buttonWrapperClass?: string;
  button?: "select" | "submit";
  placeholder?: string;
  label?: ReactNode;
  onClick?: (event: MouseEvent) => void;
  open?: boolean;
  error?: string;
  isMobile?: boolean;
  multiSelectLabel?: string;
  selectLater?: {
    value: string;
    label: string;
    selected?: boolean;
  };
  new_styles?: boolean;
  centeredFilters?: boolean;
};

export const ComboboxGroupButton = forwardRef<
  HTMLDivElement,
  ComboboxGroupButtonProps
>(
  (
    {
      children,
      labels,
      onClick,
      open,
      placeholder,
      label,
      centeredFilters,
      multiSelectLabel = centeredFilters ? "Needs" : "Specialty",
      selectLater,
      startIcon,
      endIcon,
      button = "select",
      variant = "outlined",
      buttonWrapperClass,
      error,
      isMobile,
      new_styles,
      name,
    },
    ref,
  ) => {
    return (
      <div className={`ComboboxButton relative ${buttonWrapperClass}`} ref={ref}>
        <button
          className={classnames(
            `ComboboxButton__button relative flex cursor-pointer h-full w-full md:w-fit items-center justify-between truncate md:py-2 md:px-4 ${button === "select" ? "pb-2" : "py-4"} text-sm text-gt-black ${button === "submit" ? "sbc" : ""}`,
            {
              "ComboboxButton__button--contained bg-gt-lilac rounded-lg md:border-inherit border md:border-none border-gt-black md:rounded-r-lg md:rounded-l-none h-full":
                variant === "contained",
              "bg-transparent": variant === "outlined",
            },
          )}
          role='button'
          aria-label='open menu'
          aria-haspopup='true'
          data-toggle='true'
          onClick={onClick}
          data-combobox-button={name}
        >
          <div className='flex w-full items-center justify-between gap-2 relative'>
            {startIcon && startIcon}
            <div className='ComboboxButton__value-wrapper flex flex-col items-start truncate w-full'>
              <div className='flex w-full'>
                <span
                  class={classnames("ComboboxButton__label truncate flex font-medium", {
                    "w-full": !error,
                  })}
                >
                  {label}
                </span>
                {error && (
                  <div class='flex min-w-max text-xs items-center justify-center ml-1 text-[#B35436] error-msg-mobile'>
                    <svg
                      aria-hidden='true'
                      focusable='false'
                      data-prefix='fas'
                      data-icon='diamond-exclamation'
                      role='img'
                      xmlns='http://www.w3.org/2000/svg'
                      viewBox='0 0 512 512'
                      height={10}
                      width={10}
                    >
                      <path
                        fill='currentColor'
                        d='M227.7 11.7c15.6-15.6 40.9-15.6 56.6 0l216 216c15.6 15.6 15.6 40.9 0 56.6l-216 216c-15.6 15.6-40.9 15.6-56.6 0l-216-216c-15.6-15.6-15.6-40.9 0-56.6l216-216zM256 128c-13.3 0-24 10.7-24 24V264c0 13.3 10.7 24 24 24s24-10.7 24-24V152c0-13.3-10.7-24-24-24zm32 224a32 32 0 1 0 -64 0 32 32 0 1 0 64 0z'
                      ></path>
                    </svg>
                    <span class='ml-1'>{error}</span>
                  </div>
                )}
              </div>
              {selectLater?.selected && !labels?.length ? (
                <span className='ComboboxButton__selection truncate text-wrap text-left'>
                  {selectLater.label}
                </span>
              ) : labels?.length ? (
                <span className='ComboboxButton__selection truncate text-wrap text-left'>
                  {labels.length > 1
                    ? `${multiSelectLabel} (${labels.length})`
                    : labels[0]}
                </span>
              ) : (
                <>
                  {placeholder && (
                    <span className='ComboboxButton__placeholder font-medium text-neutral-400'>
                      {placeholder}
                    </span>
                  )}
                </>
              )}
            </div>
            {endIcon && endIcon}
          </div>
        </button>
        {error && (
          <div class={classnames('md:flex absolute top-full hidden min-w-max text-xs items-center justify-center my-1 error-msg-desktop', {
            'text-[#FFFFFF]': new_styles,
            'text-[#B35436]': !new_styles
          })}>
            <svg
              aria-hidden='true'
              focusable='false'
              data-prefix='fas'
              data-icon='diamond-exclamation'
              role='img'
              xmlns='http://www.w3.org/2000/svg'
              viewBox='0 0 512 512'
              height={10}
              width={10}
            >
              <path
                fill='currentColor'
                d='M227.7 11.7c15.6-15.6 40.9-15.6 56.6 0l216 216c15.6 15.6 15.6 40.9 0 56.6l-216 216c-15.6 15.6-40.9 15.6-56.6 0l-216-216c-15.6-15.6-15.6-40.9 0-56.6l216-216zM256 128c-13.3 0-24 10.7-24 24V264c0 13.3 10.7 24 24 24s24-10.7 24-24V152c0-13.3-10.7-24-24-24zm32 224a32 32 0 1 0 -64 0 32 32 0 1 0 64 0z'
              ></path>
            </svg>
            <span class='ml-1'>{error}</span>
          </div>
        )}
        {typeof open !== "undefined" && children && children({ open })}
      </div>
    );
  },
);
